/**
 * What a household tracks. A family can turn sleep or diaper changes off
 * (many skip sleep while the babies are newborns); bottles, medicine, and
 * notes are always on. The household stores the types it turned off, so an
 * empty list means everything is tracked and a type made optional later
 * starts on. Turning a type off hides its button, summaries, milestones, and
 * checks; its entries stay in the log.
 */
import type { EntryType } from "./forgotten.ts";

export const TRACKABLE_TYPES = ["sleep", "diaper"] as const satisfies readonly EntryType[];

export type TrackableType = (typeof TRACKABLE_TYPES)[number];

/** Each optional type, on or off. */
export type Tracked = Record<TrackableType, boolean>;

/** Whether a household that turned off `untracked` still tracks `type`. */
export function isTracked(untracked: readonly TrackableType[], type: EntryType): boolean {
  return !(untracked as readonly string[]).includes(type);
}

/** Only the entries of types the household tracks: what a summary of its log may count. */
export function trackedOnly<E extends { type: EntryType }>(
  entries: readonly E[],
  untracked: readonly TrackableType[],
): E[] {
  return entries.filter((e) => isTracked(untracked, e.type));
}

/** The household's switches, from the types it turned off. */
export function tracked(untracked: readonly TrackableType[] = []): Tracked {
  return { sleep: isTracked(untracked, "sleep"), diaper: isTracked(untracked, "diaper") };
}
